import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Package, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toaster';
import { useHasRole } from '@/features/auth/AuthProvider';
import type { Category, InventoryItem } from '@/types/schemas';
import { fetchAllInventory } from './inventoryQueries';
import { addBrand, deleteBrand, fetchBrands } from './setupQueries';
import { InventoryFormDialog } from './InventoryFormDialog';
import { cn } from '@/lib/utils';

export function CategoryDetailDialog({
  category,
  open,
  onOpenChange,
}: {
  category: Category | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const canWrite = useHasRole('owner');
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [newBrandName, setNewBrandName] = useState('');
  const [productOpen, setProductOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<InventoryItem | null>(null);

  const { data: brands = [] } = useQuery({
    queryKey: ['inventory-brands', category?.id],
    queryFn: () => fetchBrands(category!.id),
    enabled: open && !!category,
  });

  const { data: products = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: fetchAllInventory,
    enabled: open,
  });

  const categoryProducts = useMemo(
    () => products.filter((p) => p.categoryId === category?.id),
    [products, category?.id],
  );

  const selectedBrand = brands.find((b) => b.id === selectedBrandId);

  const brandProducts = useMemo(() => {
    if (!selectedBrand) return [];
    return categoryProducts.filter(
      (p) =>
        p.brandId === selectedBrand.id ||
        (!p.brandId && p.brand.toLowerCase() === selectedBrand.name.toLowerCase()),
    );
  }, [categoryProducts, selectedBrand]);

  const brandCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const brand of brands) {
      counts.set(
        brand.id,
        categoryProducts.filter(
          (p) =>
            p.brandId === brand.id ||
            (!p.brandId && p.brand.toLowerCase() === brand.name.toLowerCase()),
        ).length,
      );
    }
    return counts;
  }, [brands, categoryProducts]);

  const addBrandMutation = useMutation({
    mutationFn: (name: string) => addBrand(name, category!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-brands', category?.id] });
      qc.invalidateQueries({ queryKey: ['inventory-brands'] });
      setNewBrandName('');
      toast({ title: 'Brand added', variant: 'success' });
    },
    onError: (e) => toast({ title: 'Failed', description: String(e), variant: 'destructive' }),
  });

  const removeBrand = useMutation({
    mutationFn: deleteBrand,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-brands', category?.id] });
      qc.invalidateQueries({ queryKey: ['inventory-brands'] });
      setSelectedBrandId(null);
    },
  });

  function handleOpenChange(next: boolean) {
    if (!next) {
      setSelectedBrandId(null);
      setNewBrandName('');
    }
    onOpenChange(next);
  }

  function handleAddBrand() {
    const trimmed = newBrandName.trim();
    if (!trimmed || !category) return;
    if (brands.some((b) => b.name.toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: 'Brand already exists in this category' });
      return;
    }
    addBrandMutation.mutate(trimmed);
  }

  if (!category) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{category.name}</DialogTitle>
            <DialogDescription>
              Manage brands and products under this category. Prefix: {category.codePrefix}
            </DialogDescription>
          </DialogHeader>

          {selectedBrand ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedBrandId(null)}
                  className="gap-1.5 px-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to brands
                </Button>
                {canWrite && (
                  <Button
                    variant="gold"
                    size="sm"
                    onClick={() => {
                      setEditingProduct(null);
                      setProductOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Add product
                  </Button>
                )}
              </div>

              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-sm font-medium">{selectedBrand.name}</p>
                <p className="text-xs text-muted-foreground">
                  {brandProducts.length} product{brandProducts.length === 1 ? '' : 's'}
                </p>
              </div>

              {brandProducts.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No products under this brand yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {brandProducts.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between rounded-lg border px-3 py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">{product.itemName}</p>
                        <p className="text-xs text-muted-foreground">
                          {product.sku || 'No SKU'} · ₹{product.sellingPrice} · {product.unit}
                        </p>
                      </div>
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingProduct(product);
                            setProductOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {canWrite && (
                <div className="flex gap-2">
                  <Input
                    placeholder="Add brand, e.g. Greenply"
                    value={newBrandName}
                    onChange={(e) => setNewBrandName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddBrand();
                    }}
                  />
                  <Button
                    variant="gold"
                    onClick={handleAddBrand}
                    disabled={!newBrandName.trim() || addBrandMutation.isPending}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}

              <div className="flex flex-wrap gap-1.5">
                {['Greenply', 'CenturyPly', 'Fevicol', 'Hafele'].map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => setNewBrandName(ex)}
                    className="rounded border px-2 py-0.5 text-xs text-muted-foreground hover:border-gold-400"
                  >
                    + {ex}
                  </button>
                ))}
              </div>

              {brands.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No brands yet. Add a brand to start adding products.
                </p>
              ) : (
                <div className="space-y-2">
                  {brands.map((brand) => (
                    <div
                      key={brand.id}
                      className={cn(
                        'flex items-center justify-between rounded-lg border px-3 py-2.5 transition-colors',
                        'hover:bg-muted/40',
                      )}
                    >
                      <button
                        type="button"
                        className="flex flex-1 items-center gap-2 text-left"
                        onClick={() => setSelectedBrandId(brand.id)}
                      >
                        <Package className="h-4 w-4 text-gold-600" />
                        <span className="font-medium">{brand.name}</span>
                        <Badge variant="secondary" className="text-[10px]">
                          {brandCounts.get(brand.id) ?? 0} products
                        </Badge>
                        <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                      </button>
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => {
                            if (confirm(`Delete brand "${brand.name}"?`)) removeBrand.mutate(brand.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <InventoryFormDialog
        open={productOpen}
        onOpenChange={setProductOpen}
        editing={editingProduct}
        defaultCategoryId={category.id}
        defaultBrandId={selectedBrandId ?? undefined}
      />
    </>
  );
}
